import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { authStorage } from '../lib/auth-storage'
import { env } from '../lib/env'
import { toast } from '../lib/toast'
import type { ApiErrorResponse, ApiSuccessResponse } from '../types/api'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

/**
 * Shared Axios instance. All HTTP calls go through service files — never
 * from React components directly.
 */
export const apiClient = axios.create({
  baseURL: env.REACT_APP_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])

const SILENT_SUCCESS_PATHS = ['/auth/verify-otp']

const GENERIC_SUCCESS_MESSAGES = new Set(['OK', 'Created.', 'Done.'])

const recentErrorToasts = new Map<string, number>()
const ERROR_DEDUPE_MS = 1500

function shouldToastSuccess(method: string, url: string, message: string | undefined): message is string {
  if (!MUTATION_METHODS.has(method) || !message || GENERIC_SUCCESS_MESSAGES.has(message)) {
    return false
  }

  return !SILENT_SUCCESS_PATHS.some((path) => url.includes(path))
}

function shouldToastError(message: string): boolean {
  const now = Date.now()
  const lastShown = recentErrorToasts.get(message)

  if (lastShown !== undefined && now - lastShown < ERROR_DEDUPE_MS) {
    return false
  }

  recentErrorToasts.set(message, now)

  return true
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authStorage.getToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase() ?? ''
    const url = response.config.url ?? ''
    const payload = response.data as ApiSuccessResponse<unknown> | undefined

    if (
      payload?.success === true &&
      shouldToastSuccess(method, url, payload.message)
    ) {
      toast.success(payload.message)
    }

    return response
  },
  (error: AxiosError<ApiErrorResponse>) => {
    const url = error.config?.url ?? ''
    const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/verify-otp')
    const message = getApiErrorMessage(error)
    const skipErrorToast = error.config?.skipErrorToast === true

    if (error.response?.status === 401) {
      authStorage.clear()

      if (isAuthAttempt) {
        if (message && !skipErrorToast) {
          toast.error(message)
        }

        return Promise.reject(error)
      }

      if (window.location.pathname !== '/login' && window.location.pathname !== '/verify-otp') {
        window.location.href = '/login'
      }

      return Promise.reject(error)
    }

    if (message && !skipErrorToast && shouldToastError(message)) {
      toast.error(message)
    }

    return Promise.reject(error)
  },
)

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.') {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.message ?? fallback
  }

  return fallback
}
